import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ArrowRight } from "lucide-react";
import Logo, { LogoFilterDefs } from "../components/landing/Logo";
import { useAuth } from "../lib/AuthContext";
import { formatApiError } from "../lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/admin" replace />;
  if (user === null) {
    return <div className="min-h-screen bg-[#0A0B0E] flex items-center justify-center text-white/40">...</div>;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0B0E] text-white relative overflow-hidden grain">
      <LogoFilterDefs />
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] glow-orange opacity-70" />
      </div>

      <div className="min-h-screen flex items-center justify-center p-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="flex justify-center mb-7">
            <Logo size={42} />
          </div>

          <div className="rounded-2xl bg-surface/70 border border-white/[0.07] p-8 backdrop-blur-sm">
            <div className="text-center mb-7">
              <span className="inline-block text-[11px] uppercase tracking-[0.22em] text-brand-orange/90 font-medium">
                Panel privado
              </span>
              <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-white tracking-tight">
                Iniciar sesión
              </h1>
              <p className="mt-1.5 text-sm text-white/55">
                Accede al panel de administración de leads
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-3.5">
              <Field
                id="email"
                label="Email"
                icon={Mail}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@vexlyapp.com"
                autoComplete="email"
                required
              />
              <Field
                id="password"
                label="Contraseña"
                icon={Lock}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />

              {error && (
                <p className="text-sm text-red-400" data-testid="admin-login-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="admin-login-submit"
                className="group w-full inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orangeHover disabled:opacity-70 text-white rounded-full px-6 py-3.5 text-base font-medium transition-all shadow-[0_0_24px_rgba(249,115,22,0.4)] hover:shadow-[0_0_36px_rgba(249,115,22,0.6)]"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    Entrar
                    <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="mt-6 text-center">
            <a href="/" className="text-xs text-white/40 hover:text-white/70 transition-colors">
              ← Volver a vexlyapp.com
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function Field({ id, label, icon: Icon, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-white/55 mb-1.5 uppercase tracking-wider">
        {label}
      </label>
      <div className="relative">
        <Icon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35" />
        <input
          id={id}
          name={id}
          data-testid={`admin-input-${id}`}
          {...rest}
          className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 focus:border-brand-orange/60 text-white text-[15px] placeholder:text-white/30 outline-none focus:bg-white/[0.06] transition-colors"
        />
      </div>
    </div>
  );
}
